import React, { useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Tag,
  Popconfirm,
  Typography,
  Select,
  Space,
  Alert,
  message,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Header } from "../../../../components";
import {
  useFetchChatTargets,
  useCreateChatTarget,
  useUpdateChatTarget,
  useDeleteChatTarget,
  useFetchForumTopics,
} from "../hooks/useFetch";

const { Paragraph, Text } = Typography;

function TelegramChatTargetPage() {
  const { data: targets = [], isLoading, refetch } = useFetchChatTargets();
  const { mutateAsync: createTarget, isLoading: isCreating } = useCreateChatTarget();
  const { mutateAsync: updateTarget, isLoading: isUpdating } = useUpdateChatTarget();
  const { mutateAsync: deleteTarget } = useDeleteChatTarget();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [form] = Form.useForm();
  // Topic ของกลุ่มฟอรัม — โหลดตามปุ่ม ไม่โหลดอัตโนมัติ เพราะต้องเปิด client เชื่อม Telegram จริง
  const [topics, setTopics] = useState(null);   // null = ยังไม่ได้โหลด · [] = โหลดแล้วไม่มี
  const [topicMsg, setTopicMsg] = useState('');
  const { mutateAsync: loadTopics, isLoading: isLoadingTopics } = useFetchForumTopics();

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setTopics(null);
    setTopicMsg('');
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      chatId: record.chat_id,
      topicId: record.topic_id != null ? String(record.topic_id) : '',
    });
    // มี topic เดิมอยู่ ใส่เป็นตัวเลือกตั้งต้นไว้ก่อน จะได้ไม่โชว์เป็นเลขเปล่า ๆ
    setTopics(record.topic_id != null
      ? [{ id: Number(record.topic_id), title: record.topic_name || `Topic ${record.topic_id}` }]
      : null);
    setTopicMsg('');
    setModalOpen(true);
  };

  const handleLoadTopics = async () => {
    const chatId = form.getFieldValue('chatId');
    if (!chatId) {
      message.warning('กรอก Chat ID ก่อน');
      return;
    }
    setTopicMsg('');
    try {
      const { result } = await loadTopics(chatId);
      if (!result?.isForum) {
        setTopics([]);
        setTopicMsg('กลุ่มนี้ไม่ได้เปิดโหมด Topic — ส่งเข้ากลุ่มได้เลย ไม่ต้องเลือก');
        return;
      }
      setTopics(result.topics || []);
      if (!result.topics?.length) setTopicMsg('ไม่พบ Topic ในกลุ่มนี้');
    } catch (e) {
      setTopics(null);
      setTopicMsg(e?.response?.data?.message || 'ดึงรายชื่อ Topic ไม่สำเร็จ');
    }
  };

  const handleToggle = async (record, checked) => {
    setTogglingId(record.id);
    try {
      await updateTarget({ id: record.id, enable: checked });
      message.success(checked ? "เปิดใช้งานแล้ว" : "ปิดใช้งานแล้ว");
      refetch();
    } catch (e) {
      message.error(e?.response?.data?.message || "เปลี่ยนสถานะไม่สำเร็จ");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      if (editing) {
        await updateTarget({
          id: editing.id, name: v.name, chatId: v.chatId,
          topicId: v.topicId ?? '',
          topicName: (topics || []).find((t) => String(t.id) === String(v.topicId))?.title || null,
        });
        message.success("บันทึกสำเร็จ");
      } else {
        await createTarget({
          name: v.name, chatId: v.chatId,
          topicId: v.topicId ?? '',
          topicName: (topics || []).find((t) => String(t.id) === String(v.topicId))?.title || null,
        });
        message.success("เพิ่มกลุ่มสำเร็จ");
      }
      setModalOpen(false);
      form.resetFields();
      setEditing(null);
      refetch();
    } catch (e) {
      if (e?.errorFields) return; // validation ของ antd
      // backend ส่งข้อความอธิบายมาให้แล้ว (เช่น chat_id ผิดรูปแบบ / กลุ่มซ้ำ)
      message.error(e?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteTarget(record.id);
      message.success("ลบกลุ่มแล้ว");
      refetch();
    } catch (e) {
      message.error(e?.response?.data?.message || "ลบไม่สำเร็จ");
    }
  };

  const columns = [
    {
      title: "เปิด/ปิด",
      dataIndex: "enable",
      align: "center",
      width: 90,
      render: (enable, record) => (
        <Switch
          checked={Number(enable) === 1}
          loading={togglingId === record.id}
          onChange={(checked) => handleToggle(record, checked)}
        />
      ),
    },
    {
      title: "ชื่อกลุ่ม (เจ้า)",
      dataIndex: "name",
      render: (n) => n || "-",
    },
    {
      title: "Topic",
      dataIndex: "topic_name",
      render: (name, r) =>
        r.topic_id != null
          ? <Tag color="blue">{name || `#${r.topic_id}`}</Tag>
          : <Text type="secondary">ทั้งกลุ่ม</Text>,
    },
    {
      title: "Chat ID",
      dataIndex: "chat_id",
      render: (c) => <Tag>{c}</Tag>,
    },
    {
      title: "เพิ่มเมื่อ",
      dataIndex: "create_date",
      width: 190,
      render: (t) => (t ? new Date(t).toLocaleString("th-TH") : "-"),
    },
    {
      title: "จัดการ",
      width: 130,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="ลบกลุ่มนี้?"
            description="เอเจนต์จะเลือกกลุ่มนี้ไม่ได้อีก — ถ้าแค่หยุดใช้ชั่วคราว ใช้สวิตช์ปิดแทน"
            onConfirm={() => handleDelete(record)}
            okText="ลบ"
            cancelText="ยกเลิก"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="m-4 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Header category="Admin" title="กลุ่มปลายทาง — ส่งสลิปเข้าแชท" />
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          เพิ่มกลุ่ม
        </Button>
      </div>

      <Paragraph style={{ color: "#888", marginBottom: 16 }}>
        กลุ่มที่เอเจนต์เลือกได้ตอนกดปุ่ม <Text strong>“ส่งแชท”</Text> ในหน้าจัดการเคส
        (ส่งด้วยบัญชี Telegram ส่วนตัวของเอเจนต์เอง)
        <br />
        <Text type="secondary">
          คนละระบบกับ “Telegram Bot — รับข้อความสร้างเคส”
        </Text>
      </Paragraph>

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={targets}
        columns={columns}
        pagination={false}
        bordered
      />

      <Modal
        title={editing ? "แก้ไขกลุ่มปลายทาง" : "เพิ่มกลุ่มปลายทาง"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        okText="บันทึก"
        cancelText="ยกเลิก"
        okButtonProps={{ loading: isCreating || isUpdating }}
        confirmLoading={isCreating || isUpdating}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="ชื่อกลุ่ม (เจ้า)"
            rules={[{ required: true, message: "กรุณากรอกชื่อกลุ่ม" }]}
          >
            <Input placeholder="เช่น Thunder - megashop" />
          </Form.Item>

          <Form.Item
            name="chatId"
            label="Chat ID"
            rules={[{ required: true, message: "กรุณากรอก Chat ID" }]}
            extra={
              <span style={{ fontSize: 12 }}>
                ต้องเป็น<Text strong>ตัวเลขติดลบ</Text> — supergroup ขึ้นต้น{" "}
                <Text code>-100</Text> เช่น <Text code>-1002616828422</Text> ·
                กลุ่มธรรมดาเช่น <Text code>-639523859</Text>
                <br />
                ดูได้จาก <Text code>web.telegram.org</Text> เปิดกลุ่มแล้วอ่านเลขหลัง{" "}
                <Text code>#</Text> บน address bar — ก๊อปมาทั้งก้อนรวมเครื่องหมายลบ
              </span>
            }
          >
            <Input placeholder="-1002616828422" />
          </Form.Item>

          {/* กลุ่มที่เปิดโหมดฟอรัมจะมี Topic ย่อย — ถ้าไม่เลือก ข้อความจะไปลงหัวข้อ General
              โหลดตามปุ่ม ไม่โหลดอัตโนมัติ เพราะต้องเปิดการเชื่อมต่อไปหา Telegram จริง */}
          <Form.Item
            name="topicId"
            label="Topic (เฉพาะกลุ่มที่เปิดโหมดฟอรัม)"
            extra={
              <span style={{ fontSize: 12 }}>
                เว้นว่าง = ส่งเข้ากลุ่มแบบเดิม (จะไปลงหัวข้อ General ถ้ากลุ่มเปิดฟอรัม)
              </span>
            }
          >
            <Select
              allowClear
              placeholder={topics === null ? 'กด "โหลด Topic" เพื่อดึงรายชื่อ' : 'ไม่ระบุ Topic'}
              options={(topics || []).map((t) => ({ value: String(t.id), label: `${t.title} (#${t.id})` }))}
              notFoundContent={topics === null ? 'ยังไม่ได้โหลด' : 'ไม่มี Topic'}
            />
          </Form.Item>

          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Button onClick={handleLoadTopics} loading={isLoadingTopics}>
              โหลด Topic จากกลุ่มนี้
            </Button>
            {topicMsg && <Alert type="info" showIcon message={topicMsg} />}
          </Space>
        </Form>
      </Modal>
    </div>
  );
}

export default TelegramChatTargetPage;
